import React from 'react';
import { Shield, RefreshCw, LogIn } from 'lucide-react';
import errorNotificationService from '../services/ErrorNotificationService';

class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AuthErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Use ErrorNotificationService to handle and display error
    errorNotificationService.handleError(error, {
      context: 'AuthErrorBoundary',
      category: 'authentication',
      title: 'Authentication Error',
      severity: 'error',
      duration: 10000
    });

    // Log authentication-specific errors
    this.logAuthError(error, errorInfo);
  }

  logAuthError = (error, errorInfo) => {
    console.error('Authentication Error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      authState: this.props.authState || 'unknown',
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  };

  handleRetry = () => {
    const { retryCount } = this.state;
    const maxRetries = 2;
    
    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      // After max retries, clear auth state and redirect to login
      this.clearAuthStateAndRedirect();
    }
  };

  clearAuthStateAndRedirect = () => {
    try {
      // Clear all authentication-related localStorage
      localStorage.removeItem('acctoo_user');
      localStorage.removeItem('acctoo_user_session');
      localStorage.removeItem('acctoo_session_expiry');
      localStorage.removeItem('acctoo_device_info');
      localStorage.removeItem('loginStep');
      localStorage.removeItem('loginPhoneNumber');
      localStorage.removeItem('loginEntryIdentifier');
      localStorage.removeItem('loginUserExists');
      localStorage.removeItem('loginUserDetails');
      localStorage.removeItem('loginSigninData');
      
      // Clear session storage
      sessionStorage.clear();
      
      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Error clearing auth state:', error);
      window.location.href = '/login';
    }
  };

  handleGoToLogin = () => {
    this.clearAuthStateAndRedirect();
  };

  render() {
    if (this.state.hasError) {
      const { retryCount } = this.state;
      const maxRetries = 2;
      const canRetry = retryCount < maxRetries;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <Shield className="h-16 w-16 text-orange-500" />
            </div>
            
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Authentication Error
            </h1>
            
            <p className="text-gray-600 mb-6">
              There was a problem with your authentication session. This usually happens when there's a network issue or session conflict.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg text-left">
                <h3 className="text-sm font-medium text-orange-800 mb-2">Auth Error Details:</h3>
                <p className="text-xs text-orange-700 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry Authentication ({retryCount + 1}/{maxRetries})
                </button>
              )}
              
              <button
                onClick={this.handleGoToLogin}
                className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                Sign In Again
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Your data is safe. Signing in again will restore your session.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;





